/**
 * THE CREW PIPELINE AS DATA (SCRUM-766, brief `docs/V2_CREW_PIPELINE_BRIEF.md`, epic SCRUM-647).
 *
 * Owner, 18.10.2026: «мы делаем пайплайн в котором можно менять блоки местами», «блоки можно добавлять и убирать. Гибкая
 * система», «Порядок загрузки сделаем какой то но чтобы прокатчик мог его под себя поменять», «то же самое под конкретное
 * шоу», «выполнение задачи открывается блоком, закрывается чекбоксами внутри». Owner, 20.10.2026 (SCRUM-766): our order is
 * ours and computed, the rental has a THIN LAYER over it and not a copy («копия = форк»); the layer adds its own step or block,
 * puts a note on a block and switches a block off; our new blocks appear BY DEFAULT; an addition is anchored by NAME («в фазе X,
 * после блока Y»), and when the anchor is gone the block goes to the end of the phase and SAYS so; the nine layers and the meaning
 * of a tick are not editable. Owner, 28.10.2026, replacing his 20.10 «давай пока не давать менять только добавлять»: «прокатчик
 * может переставлять блоки на свое усмотрение» — a layer moves ANY block, ours included.
 *
 * What lives here, pure, no database and no screen:
 *
 *   - the BLOCK (`CrewBlockDef`): id, name, kind (`warehouse` | `site`), what its tick means (`layer`) and what it holds;
 *   - OUR DEFAULT (`CREW_DEFAULT_PIPELINE`): the warehouse blocks in UNLOAD order and the site blocks per phase;
 *   - the PHASES of the day (`CREW_PHASE_LAYOUT`): fixed. The warehouse order is written ONCE and the four warehouse passes read
 *     it — pick and return as written, loading at the warehouse and at the venue reversed («загрузка обычно обратна выгрузке»);
 *   - a LAYER (`CrewPipelineLayer`, a document of its own): the rental's template, or the show's edit on top of it;
 *   - `resolveCrewPipeline` — ours → rental → show, each later layer over the one before;
 *   - the show's ROUTE (`CrewRoute`): where the truck is loaded and where it goes after the strike — any place (28.10.2026: «всё
 *     что угодно»); the pick happens only at the rental's own warehouse, loading wherever the gear is;
 *   - `distributeCrewItems` — items into the blocks of a phase by bucket; the bucket no block takes goes to «other»;
 *   - the TICK (`CrewTick`): (task, block, document element, who, when), an append-only FACT with its own client-made id, so two
 *     phones merge by union and the current state is DERIVED (`crewTickState`) — the base SCRUM-771 queues offline;
 *   - `crewBlockProgress` — «N of M» of a block: one tick closes an element, a block with no items closes by its own tick;
 *   - which TEMPLATE VERSION a show follows (`resolveShowCrewPipeline`, `adoptCrewTemplate`) — owner, 28.10.2026;
 *   - a PERSON's working day (`CrewWorkdayFact`, `crewWorkday`, `crewMayWorkday`): started, closed by the worker or the rental,
 *     confirmed, and the hours — owner, 28.10.2026. Who besides the rental may close days is `dayClose` of the show's layer.
 *
 * Where it does NOT live: inside the rider. The rider is the band's; the pipeline is the rental's (templates) and the show's
 * (its layer), so a layer is its own document, versioned by `CREW_PIPELINE_SCHEMA_VERSION`, and no `Rider` field moved.
 *
 * Names: a block of OURS names itself with an i18n key of `apps/web` (`{ key }`) — the one table of our default is here, not
 * split between two packages; a block a layer adds carries the words as the rental typed them (`{ text }`), drawn as written.
 */
import { z } from "zod";
import { UuidSchema } from "./schema";
export const CREW_PIPELINE_SCHEMA_VERSION = 1;
// ---------------------------------------------------------------------------------------------
// Closed vocabularies — nobody's layer edits these
// ---------------------------------------------------------------------------------------------
/** What a tick on a list block means, in the order of the day. Nine, and not editable (owner, 20.10.2026). */
export const CREW_LAYERS = ["picked", "loaded", "placed", "patched", "checked", "unpatched", "struck", "loadedback", "returned"];
const TICK_LAYERS = [...CREW_LAYERS, "step"];
/** The layers a SITE block can mean (the warehouse passes give the other four). */
export const CREW_SITE_LAYERS = ["placed", "patched", "checked", "unpatched", "struck"];
/** Where a block is worked: at the warehouse (groups of items) or at the venue (stages of work). */
export const CREW_BLOCK_KINDS = ["warehouse", "site"];
/** The three parts of the day, the tabs of the crew screen: Prep · Venue · Return. */
export const CREW_PARTS = ["out", "venue", "return"];
/** `unload` replaces `back` when the truck does not go back to the rental's warehouse (owner, 28.10.2026). */
export const CREW_PHASES = ["pick", "load", "setup", "showRun", "strike", "back", "unload"];
export const CREW_SITE_PHASES = ["setup", "showRun", "strike", "unload"];
/**
 * The warehouse groups an item falls into BY MACHINE, by its catalogue rubric (the client decides a line's bucket from its gear
 * section and the catalogue's rubric tree — `apps/web/src/crew/crew-work-model.ts`). Light, when it comes, is a new bucket here
 * and a new block in our default — additive.
 */
export const CREW_BUCKETS = ["racks", "pa", "mon", "foh", "mics", "dis", "stands", "backline", "risers", "cables", "other"];
/**
 * The block that takes every item no other block takes — the items of a block a layer removed among them. It can not be
 * removed itself: it is what keeps «an item of the agreed offer never drops out of the list» true (ticket SCRUM-766).
 */
export const CREW_FALLBACK_BLOCK = "other";
/** A tick's answer. On the way back a line is also `missing` or `broken`; `null` in a tick takes a tick back. */
export const CREW_TICK_STATES = ["done", "missing", "broken"];
const bucketBlock = (bucket, extra = {}) => ({
    id: bucket,
    kind: "warehouse",
    name: { key: `crew.block.${bucket}` },
    holds: { what: "bucket", bucket },
    ...extra,
});
const step = (id, key, extra = {}) => ({ id, kind: "site", name: { key }, holds: { what: "step" }, ...extra });
const FROM_OFFER = { hint: "crew.hint.fromOffer" };
/**
 * OUR DEFAULT, in the owner's words (18.10.2026, brief §3; Microphones 19.10.2026; Stage split into Microphones, DIs, Stands
 * 20.10.2026; the venue's three power steps 18.10.2026; Show run and the strike 18.10.2026, SCRUM-770).
 *
 * The carts and ramp: first at the pick, and the LAST block of loading, at the warehouse and at the venue (owner, 28.10.2026: «ставь
 * тележки и рампу последним блоком»; 18.10: «телега … и рампа загружается последней а выгружается первой»). Not a line of the return
 * check (`only`).
 *
 * «Unload at <place>» ends a day that does not go back to the rental's warehouse (owner, 28.10.2026: «он должен поставить блок
 * "разгрузка на площадку такую то"»): open to any link; a work block like any other — the day is closed per PERSON, not by its tick
 * (owner, 28.10.2026: «по пайплайну надо просто закрыть рабочий день человеку»; `crewWorkday`).
 */
export const CREW_DEFAULT_PIPELINE = {
    warehouse: [
        { id: "carts", kind: "warehouse", name: { key: "crew.block.carts" }, holds: { what: "step" }, only: ["pick", "load", "strike"], instanceId: { pick: "carts" } },
        bucketBlock("racks"),
        bucketBlock("pa", FROM_OFFER),
        bucketBlock("mon"),
        bucketBlock("foh", FROM_OFFER),
        bucketBlock("mics"),
        bucketBlock("dis"),
        bucketBlock("stands"),
        bucketBlock("backline"),
        bucketBlock("risers"),
        bucketBlock("cables"),
        bucketBlock("other"),
    ],
    site: {
        setup: [
            { id: "s-heavy", kind: "site", name: { key: "crew.block.unloadToPositions" }, sub: "crew.block.unloadToPositionsSub", holds: { what: "elements", which: "heavy" }, layer: "placed", two: true },
            { id: "s-mon", kind: "site", name: { key: "crew.block.paMonitorsInPlace" }, holds: { what: "elements", which: "monitor" }, layer: "placed" },
            step("power-pa", "crew.block.powerPa"),
            step("power-stage", "crew.block.powerStage"),
            step("power-foh", "crew.block.powerFoh"),
            step("pa-check", "crew.block.paCheck"),
            { id: "s-stage", kind: "site", name: { key: "crew.block.stageSet" }, sub: "crew.block.stageSetSub", holds: { what: "elements", which: "rest" }, layer: "placed" },
            { id: "s-patch", kind: "site", name: { key: "crew.block.patch" }, sub: "crew.block.patchSub", holds: { what: "inputs" }, layer: "patched" },
            { id: "s-line", kind: "site", name: { key: "crew.block.lineCheck" }, sub: "crew.block.lineCheckSub", holds: { what: "inputs" }, layer: "checked" },
        ],
        showRun: [step("show-run", "crew.block.showRun", { sub: "crew.block.showRunSub", two: true, perAct: true })],
        strike: [
            { id: "x-unpatch", kind: "site", name: { key: "crew.block.unpatch" }, holds: { what: "inputs" }, layer: "unpatched" },
            { id: "x-small", kind: "site", name: { key: "crew.block.clearSmall" }, sub: "crew.block.clearSmallSub", holds: { what: "elements", which: "rest" }, layer: "struck" },
            { id: "x-large", kind: "site", name: { key: "crew.block.clearLarge" }, sub: "crew.block.clearLargeSub", holds: { what: "elements", which: "large" }, layer: "struck", two: true },
            step("x-foh-pa", "crew.block.fohPaLast", { sub: "crew.block.fohPaLastSub", two: true }),
        ],
        unload: [step("unload-at", "crew.block.unloadAt", { open: true, keep: true, atRouteEnd: true })],
    },
};
export const CREW_PHASE_LAYOUT = [
    { id: "pick", part: "out", tag: "1", title: "crew.phase.pick", sub: "crew.phase.pickSub", warehouse: { pass: "pick", layer: "picked", prefix: "pick", reversed: false } },
    {
        id: "load",
        part: "out",
        tag: "2",
        title: "crew.phase.load",
        sub: "crew.phase.loadSub",
        warehouse: { pass: "load", layer: "loaded", prefix: "load", reversed: true, load: true },
        gate: { id: "depart-out", name: "crew.gate.depart", gate: "out_departed", layer: "loaded" },
    },
    { id: "setup", part: "venue", tag: "3", title: "crew.phase.setup", sub: "crew.phase.setupSub", banner: "crew.banner.powerOn", site: "setup" },
    { id: "showRun", part: "venue", tag: "4", title: "crew.phase.showRun", sub: "crew.phase.showRunSub", site: "showRun" },
    {
        id: "strike",
        part: "venue",
        tag: "5",
        title: "crew.phase.strike",
        sub: "crew.phase.strikeSub",
        banner: "crew.banner.powerOff",
        site: "strike",
        warehouse: { pass: "strike", layer: "loadedback", prefix: "out", reversed: true, load: true, wrap: "crew.block.loadOut" },
        gate: { id: "depart-venue", name: "crew.gate.departVenue", gate: "venue_departed", layer: "loadedback" },
    },
    { id: "back", part: "return", tag: "6", title: "crew.phase.back", sub: "crew.phase.backSub", warehouse: { pass: "back", layer: "returned", prefix: "ret", reversed: false, ret: true } },
    // In place of `back` when the truck goes elsewhere; the two never stand together.
    { id: "unload", part: "return", tag: "6", title: "crew.phase.unload", sub: "crew.phase.unloadSub", site: "unload" },
];
// ---------------------------------------------------------------------------------------------
// A layer — the rental's template, or the show's edit. Its own document.
// ---------------------------------------------------------------------------------------------
const DEF_ID = /^[a-z0-9][a-z0-9-]{0,47}$/;
const DefIdSchema = z.string().regex(DEF_ID, "a block id: a-z, 0-9 and -, up to 48");
/**
 * Ids the day gives its INSTANCES and gates (`pick-pa`, `load-pa`, `out-pa`, `ret-pa`, `depart-out`), plus the phase names, are
 * not an own block's: a rental's site block called `pick-pa` would share a tick key with our P.A. pick. Reserved, not checked
 * against today's instances, so a later block of ours can not collide either.
 */
export const CREW_RESERVED_BLOCK_PREFIXES = ["pick-", "load-", "out-", "ret-", "setup-", "showrun-", "strike-", "back-", "depart-"];
const OwnBlockIdSchema = DefIdSchema.refine((id) => !CREW_RESERVED_BLOCK_PREFIXES.some((p) => id.startsWith(p)), {
    message: `an own block's id may not start with ${CREW_RESERVED_BLOCK_PREFIXES.join(" ")}`,
});
const notBlank = (s) => s.trim().length > 0;
/** Words a person typed: kept as typed (never trimmed or corrected), only not blank. */
const typed = (max) => z.string().min(1).max(max).refine(notBlank, "must not be blank");
const CrewOwnLineSchema = z
    .object({
    id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/, "a line id: letters, digits, _ and -, up to 40"),
    text: typed(200),
    qty: z.number().int().min(1).max(9999),
})
    .strict();
/** A block a layer adds: its own step, or its own block with its own lines («модуль» — генератор, катеринг; 20.10.2026). */
export const CrewOwnBlockSchema = z
    .object({
    id: OwnBlockIdSchema,
    kind: z.enum(CREW_BLOCK_KINDS),
    name: typed(80),
    /** A site block's phase; a warehouse block has none — it is in all four warehouse passes. */
    phase: z.enum(CREW_SITE_PHASES).optional(),
    /** A site block with lines: what its tick means. */
    layer: z.enum(CREW_SITE_LAYERS).optional(),
    /** Absent: a work step. */
    lines: z.array(CrewOwnLineSchema).min(1).max(200).optional(),
    two: z.boolean().optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.kind === "site" && b.phase === undefined)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["phase"], message: "a site block names its phase" });
    if (b.kind === "warehouse" && b.phase !== undefined)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["phase"], message: "a warehouse block is in every warehouse pass; it has no phase" });
    if (b.kind === "warehouse" && b.layer !== undefined)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["layer"], message: "a warehouse block's layer is its pass's" });
    if (b.kind === "site" && b.lines !== undefined && b.layer === undefined)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["layer"], message: "a site block with lines says what its tick means" });
    if (b.lines === undefined && b.layer !== undefined)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["layer"], message: "a step has no layer" });
    const seen = new Set();
    for (const [i, l] of (b.lines ?? []).entries()) {
        if (seen.has(l.id))
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["lines", i, "id"], message: "line ids are unique in a block" });
        seen.add(l.id);
    }
});
/**
 * A PLACE the truck is loaded at or goes back to. Owner, 28.10.2026: «у него может быть загрузка не на складе а допустим на другой
 * площадке», then «всё что угодно» — so the place itself is free, not a list of kinds. What the model needs to know is only
 * whether it is the rental's OWN warehouse (then the pick happens there, at one of its `stock_locations` when named) or
 * somewhere else — another show's venue (`show`), a venue on record (`venue`), or the rental's own words (`text`: another
 * warehouse, a partner's yard, anything; drawn as typed, never trimmed).
 */
export const CrewPlaceSchema = z.discriminatedUnion("at", [
    z.object({ at: z.literal("rental_warehouse"), location: UuidSchema.optional() }).strict(),
    z.object({ at: z.literal("elsewhere"), show: UuidSchema.optional(), venue: UuidSchema.optional(), text: typed(200).optional() }).strict(),
]);
/** Where the day's gear comes from and where it goes after the strike. Absent ends: the rental's warehouse, as always. */
export const CrewRouteSchema = z
    .object({ from: CrewPlaceSchema.optional(), to: CrewPlaceSchema.optional() })
    .strict()
    .superRefine((r, ctx) => {
    for (const end of ["from", "to"]) {
        const place = r[end];
        if (place?.at === "elsewhere" && place.show === undefined && place.venue === undefined && place.text === undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [end], message: "a place elsewhere names a show, a venue or the rental's words" });
        }
    }
});
/** A role a block is assigned to: a crew link's role, or the id of one of the rental's own roles (`rental_crew_roles`). */
export const CREW_LINK_ROLES = ["crew", "driver", "other"];
export const CrewAssignRoleSchema = z.union([z.enum(CREW_LINK_ROLES), UuidSchema]);
/** Up to this many people and this many role entries (a role repeated counts each time: Crew x2 is two) in one `assign`. */
export const CREW_ASSIGN_MAX = 10;
/** `after: null` is the start of the phase; `after` names a block (ours or a layer's) of the SAME phase. */
export const CrewLayerOpSchema = z.discriminatedUnion("op", [
    z.object({ op: z.literal("add"), block: CrewOwnBlockSchema, after: DefIdSchema.nullable() }).strict(),
    /** Switch a block off. Opt-out: what is not removed — our blocks added later included — is there. */
    z.object({ op: z.literal("remove"), id: DefIdSchema }).strict(),
    z.object({ op: z.literal("move"), id: DefIdSchema, after: DefIdSchema.nullable() }).strict(),
    /** A note on the block — where a rental's «rules» live (20.10.2026). */
    z.object({ op: z.literal("note"), id: DefIdSchema, text: typed(500) }).strict(),
    /**
     * Who is meant to do the block — a hint, never a lock (19.10.2026: «давай сами там поймём»). By ROLE or by NAME (owner, 30.10.2026:
     * «назначает там ответственных на блоки по роли или по имени если уже кто то подписан»): `roles` — a link's role (`crew`, `driver`,
     * `other`) or one of the rental's own roles (its id); `who` — crew links of the show, people who already took one. A template names
     * roles only: people appear in a show. The last assign of a block wins whole; one with neither says «nobody in particular».
     * A role REPEATED is how many of it are meant (SCRUM-939, owner, 30.10.2026: «2 техника»): `["crew","crew","driver"]` is Crew x2, Driver x1.
     * Still a hint, never a lock; up to ten entries in all.
     */
    z.object({ op: z.literal("assign"), id: DefIdSchema, who: z.array(UuidSchema).max(CREW_ASSIGN_MAX).optional(), roles: z.array(CrewAssignRoleSchema).max(CREW_ASSIGN_MAX).optional() }).strict(),
    /** The rental's own name for a block, drawn as typed («Unload at <place>» first of all — 28.10.2026). */
    z.object({ op: z.literal("rename"), id: DefIdSchema, name: typed(80) }).strict(),
    /** Open a block to any crew link of the show — the next shift (28.10.2026), or close it again. */
    z.object({ op: z.literal("open"), id: DefIdSchema, open: z.boolean() }).strict(),
]);
/**
 * Who may close the day besides the rental, which always may (owner, 28.10.2026): the driver, and links named by the rental — a crew
 * chief. The SHOW's layer only.
 */
export const CrewDayCloseSchema = z.object({ driver: z.boolean().optional(), tasks: z.array(UuidSchema).max(20).optional() }).strict();
/** The template VERSION a show follows (owner, 28.10.2026: edits apply «к выбранным»). The SHOW's layer only. */
export const CrewTemplateRefSchema = z.object({ id: UuidSchema, version: z.number().int().min(1) }).strict();
export const CrewPipelineLayerSchema = z
    .object({
    schema_version: z.literal(CREW_PIPELINE_SCHEMA_VERSION),
    ops: z.array(CrewLayerOpSchema).max(500),
    /** The SHOW's layer only: where the truck is loaded and where it goes back to (28.10.2026). Additive, optional. */
    route: CrewRouteSchema.optional(),
    /** The SHOW's layer only: who besides the rental may close the day (28.10.2026). */
    close: CrewDayCloseSchema.optional(),
    /** The SHOW's layer only: the template version it follows until the rental applies another (28.10.2026). */
    template: CrewTemplateRefSchema.optional(),
})
    .strict();
/** A rental keeps several templates (owner, 20.10.2026: a club for 200 and a festival stage are different orders). */
export const CrewPipelineTemplateSchema = z.object({ name: typed(80), layer: CrewPipelineLayerSchema }).strict();
/**
 * One saved VERSION of a rental's template. An edit makes a new version; a show keeps the one it adopted until the rental applies the
 * new one to it — nothing rewrites a running or planned show silently (owner, 28.10.2026: «к выбранным»).
 */
export const CrewTemplateVersionSchema = z.object({ id: UuidSchema, version: z.number().int().min(1), name: typed(80), layer: CrewPipelineLayerSchema }).strict();
export const CREW_SEQUENCES = ["warehouse", ...CREW_SITE_PHASES];
function ownDef(b) {
    return {
        id: b.id,
        kind: b.kind,
        name: { text: b.name },
        holds: b.lines ? { what: "own", lines: b.lines } : { what: "step" },
        ...(b.layer ? { layer: b.layer } : {}),
        ...(b.two ? { two: true } : {}),
    };
}
/**
 * The day's blocks for one show: our default, the rental's template over it, the show's edit over that. Pure; an op that can
 * not be applied is not applied and is named in `issues`.
 */
export function resolveCrewPipeline(input = {}) {
    const { seqs, issues } = crewSequences(input);
    // (the phases below are built from the laid-out sequences)
    return phasesOf(input, seqs, issues);
}
/**
 * The five sequences a layer edits, as our default and the layers lay them out: the warehouse blocks in UNLOAD order (every warehouse
 * pass reads them) and the blocks of each site phase. What `resolveCrewPipeline` builds the day from, and what the rental's editor of
 * its order of work shows (`crew-order-edit.ts`) — one laying-out, so the editor can not show an order the day would not have.
 */
export function crewSequences(input = {}) {
    const base = input.base ?? CREW_DEFAULT_PIPELINE;
    const seqs = {
        warehouse: base.warehouse.map((def) => ({ def, origin: "ours", notes: [] })),
        setup: base.site.setup.map((def) => ({ def, origin: "ours", notes: [] })),
        showRun: base.site.showRun.map((def) => ({ def, origin: "ours", notes: [] })),
        strike: base.site.strike.map((def) => ({ def, origin: "ours", notes: [] })),
        unload: (base.site.unload ?? []).map((def) => ({ def, origin: "ours", notes: [] })),
    };
    const issues = [];
    const find = (id) => {
        for (const seq of Object.keys(seqs)) {
            const at = seqs[seq].findIndex((e) => e.def.id === id);
            if (at >= 0)
                return { seq, at };
        }
        return null;
    };
    /** Put `entry` after `after` in `seq`; a missing anchor sends it to the end and says so. */
    const place = (seq, entry, after, issue) => {
        const list = seqs[seq];
        if (after === null) {
            list.unshift(entry);
            return;
        }
        const at = list.findIndex((e) => e.def.id === after);
        if (at >= 0) {
            list.splice(at + 1, 0, entry);
            return;
        }
        entry.anchorLost = true;
        list.push(entry);
        issues.push({ ...issue, kind: "anchor-lost" });
    };
    const apply = (layer, from) => {
        for (const [i, op] of (layer?.ops ?? []).entries()) {
            const id = op.op === "add" ? op.block.id : op.id;
            const named = { from, op: i, id };
            if (op.op === "add") {
                if (find(id)) {
                    issues.push({ ...named, kind: "duplicate-id" });
                    continue;
                }
                const seq = op.block.kind === "warehouse" ? "warehouse" : op.block.phase;
                place(seq, { def: ownDef(op.block), origin: from, notes: [] }, op.after, named);
                continue;
            }
            if (op.op === "assign" && from === "rental" && (op.who?.length ?? 0) > 0) {
                issues.push({ ...named, kind: "people-per-show" });
                continue;
            }
            const found = find(id);
            if (!found) {
                issues.push({ ...named, kind: "unknown-block" });
                continue;
            }
            const entry = seqs[found.seq][found.at];
            if (op.op === "remove") {
                if (id === CREW_FALLBACK_BLOCK)
                    issues.push({ ...named, kind: "keeps-the-rest" });
                else if (entry.def.keep)
                    issues.push({ ...named, kind: "keeps-the-day-end" });
                else
                    seqs[found.seq].splice(found.at, 1);
            }
            else if (op.op === "move") {
                // Any block, ours included (owner, 28.10.2026). It moves within its own phase; it keeps its origin, so a note or a new
                // block of ours still finds it. The warehouse passes read the moved order, loading reversed.
                seqs[found.seq].splice(found.at, 1);
                delete entry.anchorLost;
                place(found.seq, entry, op.after, named);
            }
            else if (op.op === "note") {
                entry.notes.push({ text: op.text, from });
            }
            else if (op.op === "rename") {
                entry.name = { text: op.name };
            }
            else if (op.op === "open") {
                entry.open = op.open;
            }
            else {
                entry.assigned = [...(op.who ?? [])];
                entry.roles = [...(op.roles ?? [])];
            }
        }
    };
    apply(input.rental, "rental");
    apply(input.show, "show");
    return { seqs, issues };
}
function phasesOf(input, seqs, issues) {
    if (input.rental?.route)
        issues.push({ from: "rental", op: -1, id: "route", kind: "route-per-show" });
    if (input.rental?.close)
        issues.push({ from: "rental", op: -1, id: "close", kind: "close-per-show" });
    if (input.rental?.template)
        issues.push({ from: "rental", op: -1, id: "template", kind: "template-per-show" });
    const WAREHOUSE = { at: "rental_warehouse" };
    const named = input.show?.route ?? {};
    const route = { from: named.from ?? WAREHOUSE, to: named.to ?? WAREHOUSE };
    const fromOwn = route.from.at === "rental_warehouse";
    const loadedByAnotherShow = route.from.at === "elsewhere" && route.from.show !== undefined;
    const phaseHappens = {
        pick: fromOwn,
        load: !loadedByAnotherShow,
        setup: true,
        showRun: true,
        strike: true,
        back: route.to.at === "rental_warehouse",
        unload: route.to.at === "elsewhere",
    };
    const placeOf = (id) => (id === "pick" || id === "load" ? named.from : id === "back" || id === "unload" ? named.to : undefined);
    const common = (e) => ({
        def: e.def.id,
        kind: e.def.kind,
        name: e.name ?? e.def.name,
        ...(e.def.sub ? { sub: e.def.sub } : {}),
        ...(e.def.hint ? { hint: e.def.hint } : {}),
        ...(e.def.two ? { two: true } : {}),
        ...(e.def.perAct ? { perAct: true } : {}),
        origin: e.origin,
        notes: e.notes.map((n) => ({ ...n })),
        ...(e.assigned?.length ? { assigned: [...e.assigned] } : {}),
        ...(e.roles?.length ? { roles: [...e.roles] } : {}),
        ...(e.anchorLost ? { anchorLost: true } : {}),
        ...((e.open ?? e.def.open) ? { open: true } : {}),
        ...(e.def.atRouteEnd ? { place: route.to } : {}),
    });
    const phases = CREW_PHASE_LAYOUT.filter((ph) => phaseHappens[ph.id]).map((ph) => {
        const blocks = [];
        if (ph.site) {
            for (const e of seqs[ph.site]) {
                const isStep = e.def.holds.what === "step";
                blocks.push({
                    id: e.def.id,
                    phase: ph.id,
                    shape: isStep ? "step" : "list",
                    layer: isStep ? "step" : e.def.layer,
                    ...common(e),
                    ...(isStep ? {} : { holds: e.def.holds }),
                });
            }
        }
        const pass = ph.warehouse;
        if (pass) {
            const order = pass.reversed ? [...seqs.warehouse].reverse() : seqs.warehouse;
            for (const e of order) {
                if (e.def.only && !e.def.only.includes(pass.pass))
                    continue;
                const isStep = e.def.holds.what === "step";
                blocks.push({
                    id: e.def.instanceId?.[pass.pass] ?? `${pass.prefix}-${e.def.id}`,
                    phase: ph.id,
                    shape: isStep ? "step" : "list",
                    layer: isStep ? "step" : pass.layer,
                    ...common(e),
                    ...(isStep ? {} : { holds: e.def.holds }),
                    ...(pass.wrap ? { wrap: pass.wrap } : {}),
                    ...(pass.load && !isStep ? { load: true } : {}),
                    ...(pass.ret && !isStep ? { ret: true } : {}),
                });
            }
        }
        if (ph.gate) {
            blocks.push({
                id: ph.gate.id,
                def: ph.gate.id,
                kind: ph.part === "out" ? "warehouse" : "site",
                phase: ph.id,
                shape: "gate",
                layer: ph.gate.layer,
                name: { key: ph.gate.name },
                gate: ph.gate.gate,
                origin: "ours",
                notes: [],
            });
        }
        const place = placeOf(ph.id);
        return { id: ph.id, part: ph.part, tag: ph.tag, title: ph.title, sub: ph.sub, ...(ph.banner ? { banner: ph.banner } : {}), ...(place ? { place } : {}), blocks };
    });
    const grant = input.show?.close;
    const dayClose = { rental: true, driver: grant?.driver ?? false, tasks: [...(grant?.tasks ?? [])] };
    return { phases, route, dayClose, issues };
}
/**
 * May this one tick this block? Any block of this show's day: yes, for the rental and for any crew link — `assign` is a hint, never a
 * lock, and no block's tick closes anything (the day is closed per person, `crewWorkday`). No such block: no.
 */
export function crewMayTick(pipeline, blockId, _who) {
    return pipeline.phases.some((ph) => ph.blocks.some((b) => b.id === blockId));
}
/**
 * Is this block meant for this link («your block» on the phone)? Named by the link, or by its role or the rental's own role it was made
 * for. A hint, never a lock: anyone of the show ticks any block (`crewMayTick`).
 */
export function crewBlockIsFor(block, who) {
    // Ids are uuids: the same id in another case is the same id (a layer stored by hand, a link's id from another source).
    const task = who.task.toLowerCase();
    const own = who.crewRole?.toLowerCase();
    if (block.assigned?.some((a) => a.toLowerCase() === task))
        return true;
    return (block.roles ?? []).some((r) => r === who.role || (own !== undefined && r.toLowerCase() === own));
}
/**
 * A show's day from the template VERSION it adopted (`show.template`), not the latest: an edit to a template reaches only the shows the
 * rental applies it to (`adoptCrewTemplate`). The adopted version gone — our default and an issue, never the latest instead. Says which
 * template the show is from (owner: «показывать из какого темплейта»).
 */
export function resolveShowCrewPipeline(input = {}) {
    const pin = input.show?.template;
    const base = input.base ? { base: input.base } : {};
    const show = input.show ? { show: input.show } : {};
    if (!pin)
        return { ...resolveCrewPipeline({ ...base, ...show }), template: null };
    const versions = (input.templates ?? []).filter((t) => t.id === pin.id);
    const latest = versions.length > 0 ? Math.max(...versions.map((t) => t.version)) : null;
    const adopted = versions.find((t) => t.version === pin.version);
    if (!adopted) {
        const p = resolveCrewPipeline({ ...base, ...show });
        p.issues.push({ from: "rental", op: -1, id: pin.id, kind: "template-version-missing" });
        return { ...p, template: { id: pin.id, version: pin.version, name: null, latest } };
    }
    return { ...resolveCrewPipeline({ ...base, rental: adopted.layer, ...show }), template: { id: pin.id, version: pin.version, name: adopted.name, latest } };
}
/** The rental applies a template version to ONE show it chose: a new show layer, the old one untouched; its own edits and route kept. */
export function adoptCrewTemplate(show, template) {
    return { ...(show ?? { schema_version: CREW_PIPELINE_SCHEMA_VERSION, ops: [] }), template: { id: template.id, version: template.version } };
}
/**
 * The items of the warehouse blocks of one phase, by bucket: each to the first block that takes its bucket, and to «other» when
 * no block does — a block a layer removed included. Every item is in exactly one block; none is lost. Blocks of other kinds
 * (steps, a layer's own lines, the site) are not in the map.
 */
export function distributeCrewItems(phase, items) {
    const out = new Map();
    const byBucket = new Map();
    let fallback;
    for (const b of phase.blocks) {
        if (b.holds?.what !== "bucket")
            continue;
        out.set(b.id, []);
        if (!byBucket.has(b.holds.bucket))
            byBucket.set(b.holds.bucket, b.id);
        if (b.def === CREW_FALLBACK_BLOCK)
            fallback = b.id;
    }
    if (fallback === undefined)
        return out; // not a warehouse phase
    for (const item of items)
        out.get(byBucket.get(item.bucket) ?? fallback).push(item.key);
    return out;
}
const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
const ID = "[A-Za-z0-9_-][A-Za-z0-9._-]*";
const ELEMENT_FORMS = [
    [new RegExp(`^e\\.(${UUID})$`), (m) => ({ kind: "node", uuid: m[1] })],
    [new RegExp(`^l\\.(${ID})$`), (m) => ({ kind: "line", id: m[1] })],
    [new RegExp(`^c\\.(\\d+)\\.(${ID})$`), (m) => ({ kind: "kit", catalogId: Number(m[1]), line: m[2] })],
    [/^a\.(\d+)$/, (m) => ({ kind: "apart", catalogId: Number(m[1]) })],
    [/^s\.([a-z_]+)\.([a-z_]+)$/, (m) => ({ kind: "system", slot: m[1], part: m[2] })],
    [/^i\.(\d+)$/, (m) => ({ kind: "input", channel: Number(m[1]) })],
    [new RegExp(`^p\\.(${UUID})$`), (m) => ({ kind: "patch", uuid: m[1] })],
    [/^o\.([a-z0-9][a-z0-9-]*)\.([A-Za-z0-9][A-Za-z0-9_-]*)$/, (m) => ({ kind: "own", block: m[1], line: m[2] })],
];
const STEP_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/;
/**
 * A line the warehouse splits over several places («SM58 ×16: 4 in Mic case 1, 4 in Mic case 2, 8 on the shelf») is picked per
 * place, and each part is a mark of its own: `<line>@<place id>`, or `@none` for the part that lies where the warehouse knows no place
 * (Organize warehouse, 29.10.2026). Only on the pick layer, where the list is cut by place; the loading and the return are by kind
 * of gear. A line that stands in ONE place keeps its plain key, so marks made before this are not lost.
 */
const SPLIT_PLACE = new RegExp(`^(.+)@(?:${UUID}|none)$`);
/** What a tick's element names, or `null` when the key is no element of a document. */
export function parseCrewElement(layer, element) {
    if (element.length > 180)
        return null;
    if (layer === "step")
        return STEP_ID.test(element) ? { kind: "step", block: element } : null;
    if (layer === "picked") {
        const part = SPLIT_PLACE.exec(element);
        if (part) {
            // only a line of a list is split; a plan node or a patch point is not
            const whole = parseCrewElement(layer, part[1]);
            return whole && (whole.kind === "line" || whole.kind === "kit" || whole.kind === "apart" || whole.kind === "system" || whole.kind === "own") ? whole : null;
        }
    }
    for (const [re, read] of ELEMENT_FORMS) {
        const m = re.exec(element);
        if (m)
            return read(m);
    }
    return null;
}
/** The key of a line of a layer's own block. */
export const crewOwnLineKey = (block, line) => `o.${block}.${line}`;
/** The server key of a tick: `<layer>:<element>` — what `crew_task_checks.key` has held since SCRUM-762. */
export const crewTickKey = (layer, element) => `${layer}:${element}`;
const CrewTickFields = z.object({
    /** Made by the phone when the hand ticked (`crypto.randomUUID()`): the same fact sent twice is one fact. */
    id: UuidSchema,
    /** The crew task (the link) the tick was made under. */
    task: UuidSchema,
    /** The block it was ticked in. Context, not identity: the same element is closed whichever block shows it. */
    block: z.string().regex(STEP_ID, "a block instance id"),
    layer: z.enum(TICK_LAYERS),
    element: z.string().min(1).max(180),
    /** `null`: the tick taken back — a fact too (SCRUM-773; 873: «снимается у всей бригады»). */
    state: z.enum(CREW_TICK_STATES).nullable(),
    /** Part of a line; absent = all of it. */
    count: z.number().int().min(1).max(100000).optional(),
    /** The user who ticked. */
    by: UuidSchema,
    /**
     * When the hand ticked — the phone's clock, with its offset. TRUSTED (owner, 28.10.2026): stored as pressed. Only the FORMAT is checked: a
     * year of the instant (UTC) from 0001 to 9998 - year 0000 is no time for Postgres (it would fail the whole batch) and year 9999 would
     * freeze the element for the whole crew.
     */
    at: z
        .string()
        .datetime({ offset: true })
        .refine((v) => {
        const year = new Date(Date.parse(v)).getUTCFullYear();
        return Number.isFinite(year) && year >= 1 && year <= 9998;
    }, "a year from 0001 to 9998"),
});
/** The rules that look across fields; the same for the fact and for its wire form. */
function checkCrewTick(t, ctx) {
    if (t.state === null && t.count !== undefined)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["count"], message: "a tick taken back has no count" });
    if (parseCrewElement(t.layer, t.element) === null)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["element"], message: "not an element of a document" });
}
export const CrewTickSchema = CrewTickFields.strict().superRefine(checkCrewTick);
/**
 * The tick as the PHONE sends it (SCRUM-771): the fact without its `task` and its `by`. Those two are the server's to write — from the
 * link the request came by and from the session — so a body that names either is refused (`.strict()`), never trusted.
 */
export const CrewTickInputSchema = CrewTickFields.omit({ task: true, by: true }).strict().superRefine(checkCrewTick);
/** Ticks in the one order every phone and the server agree on: the time of the hand, then the id. */
function byTime(a, b) {
    return Date.parse(a.at) - Date.parse(b.at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}
const canon = (t) => JSON.stringify([t.id, t.task, t.block, t.layer, t.element, t.state, t.count ?? null, t.by, t.at]);
/**
 * Two phones' ticks — or the phone's queue and the server's — as one log: the UNION by the tick's own id, in time order.
 * Commutative, associative and idempotent, so the order of arrival, a resend and a retry change nothing, and no tick is lost.
 * Two different facts under one id (a client bug) resolve the same way everywhere: the smaller by content.
 */
export function mergeCrewTicks(...logs) {
    const byId = new Map();
    for (const log of logs) {
        for (const t of log) {
            const was = byId.get(t.id);
            if (!was || canon(t) < canon(was))
                byId.set(t.id, t);
        }
    }
    return [...byId.values()].sort(byTime);
}
/**
 * What is ticked now, per `<layer>:<element>`: the latest tick wins — a tick taken back is the latest too, and then nothing is
 * ticked. Last write wins, with no locks and no summing of counts (SCRUM-873, owner 28.10.2026: «873 да»). Derived, never stored
 * as truth: from the same ticks every phone derives the same state.
 */
export function crewTickState(ticks) {
    const now = new Map();
    for (const t of [...ticks].sort(byTime)) {
        const key = crewTickKey(t.layer, t.element);
        if (t.state === null)
            now.delete(key);
        else
            now.set(key, { state: t.state, ...(t.count !== undefined ? { count: t.count } : {}), by: t.by, at: t.at, id: t.id, task: t.task, block: t.block });
    }
    return now;
}
/** Every tick on one element, in time order — who touched it and when, nothing dropped. */
export function crewTickHistory(ticks, key) {
    return ticks.filter((t) => crewTickKey(t.layer, t.element) === key).sort(byTime);
}
// ---------------------------------------------------------------------------------------------
// The wire — a tick that crosses a bad network (SCRUM-771)
// ---------------------------------------------------------------------------------------------
/**
 * The kinds of fact the phone's queue carries, each in a typed envelope `{ kind, ...fact }`. One queue, one endpoint
 * (`POST /crew-tasks/:token/facts`), one answer per fact: a later kind (the workday close) is a name added here and a judge on the
 * server, not a second queue.
 */
export const CREW_FACT_KINDS = ["tick"];
/** At most this many facts to a request: the phone sends its queue in chunks, the server refuses a longer one with 400. */
export const CREW_TICK_BATCH_MAX = 100;
/**
 * Why the server did not take a fact. TECHNICAL reasons only (owner, 28.10.2026: «мы не придумываем никакую логику за людей»): a
 * departed truck and the age of a tap are not reasons - what the person did on the phone is what synchronises, the last hand wins. The
 * phone shows these in words and does not send the fact again.
 */
export const CREW_FACT_REJECTIONS = ["invalid", "id_conflict", "unknown_kind"];
/** Pieces ticked done on this layer. ONE tick — one worker's — closes the element. */
export function crewElementTicked(state, layer, element, qty) {
    const t = state.get(crewTickKey(layer, element));
    if (!t || t.state !== "done")
        return 0;
    return Math.min(qty, t.count ?? qty);
}
/** On the way back a line is settled by done, missing or broken. */
export function crewElementSettled(state, layer, element, qty) {
    const t = state.get(crewTickKey(layer, element));
    if (t && t.state !== "done")
        return qty;
    return crewElementTicked(state, layer, element, qty);
}
/**
 * «N of M» of one block. A step (a block with no items) is 1 and closes by its own tick; a list counts pieces; a gate is an
 * event, not work — 0 of 0.
 */
export function crewBlockProgress(block, state, qty) {
    if (block.shape === "gate")
        return { done: 0, total: 0 };
    if (block.shape === "step")
        return { done: state.has(crewTickKey("step", block.id)) ? 1 : 0, total: 1 };
    let done = 0;
    let total = 0;
    for (const item of block.items ?? []) {
        const q = qty.get(item) ?? 1;
        total += q;
        done += block.ret ? crewElementSettled(state, block.layer, item, q) : crewElementTicked(state, block.layer, item, q);
    }
    return { done, total };
}
function validateWith(schema, input) {
    let candidate = input;
    if (typeof input === "string") {
        try {
            candidate = JSON.parse(input);
        }
        catch (err) {
            return { success: false, errors: [{ path: "$", message: `malformed JSON: ${err.message}` }] };
        }
    }
    const parsed = schema.safeParse(candidate);
    if (parsed.success)
        return { success: true, data: parsed.data };
    return { success: false, errors: parsed.error.issues.map((i) => ({ path: i.path.join("."), message: i.message })) };
}
export const validateCrewPipelineLayer = (input) => validateWith(CrewPipelineLayerSchema, input);
export const validateCrewPipelineTemplate = (input) => validateWith(CrewPipelineTemplateSchema, input);
export const validateCrewTick = (input) => validateWith(CrewTickSchema, input);
// ---------------------------------------------------------------------------------------------
// A person's hours — in the owner's words (28.10.2026)
// ---------------------------------------------------------------------------------------------
/**
 * The owner, 28.10.2026: «по пайплайну надо просто закрыть рабочий день человеку. Это может сделать прокатчик»; «или это может сделать
 * сам человек, потом прокатчик подтвердит. Нам важно время во сколько закрылся день»; «если прокатчик его не закрывает (спит или занят)
 * то работник может сам закрыть день. Он отработал столько то часов»; «Если он его подтверждает то всё окей. Если он закрывает
 * работнику день то тоже всё окей»; «если работник потом нажал начать у него считается следующая смена. ее должен будет подтвердить
 * прокатчик»; «это может быть хоть сразу»; «просто новые часы идут»; «петя подвтверждает. потом додумаем про начальника его»; «как
 * человек мог закрыть день что начало неизвестно? если у он у него был открыт то было время открытия»; «мы не придумываем никакую
 * логику за людей»; «не выдумывай хуйни».
 *
 * Exactly that, and nothing more:
 *   1. The worker presses «Начать» (`start`) — the start of his hours. «Закрыть день» exists only after «Начать».
 *   2. The day is closed (`close`, naming the shift it closes) by the worker or by the rental — at the moment of pressing, on the open
 *      shift. The FIRST close pressed counts, whoever pressed it («Нам важно время во сколько закрылся день»); a close of a shift no
 *      longer open counts for nothing. The rental is told «so-and-so closed the day» and confirms.
 *   3. Confirmed (`confirm`, naming the close that counts) or closed first by the rental — FINAL. A confirmation carries no times: the
 *      hours are from «Начать» to the close — facts never change, so nothing can rewrite them.
 *   4. «Начать» after a close is new hours, «хоть сразу»; the rental confirms them too.
 *   5. A crew chief or a driver with the right (the show layer's `close`) confirms, his own hours included.
 * A mistaken «Закрыть день» (owner: «оба варианта возможно. Даже потом прокатчик может объединить его часы и удалить дырку чтобы
 * ошибочное нажатие не лишило человека денег»): the rental (or one with the right) REOPENS the counted close (`reopen`, `of`) — the
 * first close pressed after it counts; or MERGES two of the person's shifts one after the other (`merge`, `shifts: [A, B]`) — one final
 * shift from A's «Начать» to B's close, the gap paid. The worker's own way is just «Начать» again.
 *
 * Append-only facts with their own id made where they were pressed, like the ticks: a phone offline queues them, two sources merge by
 * union (SCRUM-771). `as` and `by` are SET BY THE SERVER from who is signed in, never taken from the phone; once stamped, the server does
 * not change a fact (the phone's copy and the server's must stay the same fact), and keeps the time it received it apart. `day` is the
 * date of the work as the phone has it, for the report.
 */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const workdayBase = {
    id: UuidSchema,
    /** Whose hours: the crew link. */
    task: UuidSchema,
    /** The date of the work as the phone has it, `YYYY-MM-DD` — for the report. */
    day: z.string().regex(ISO_DAY, "a date as YYYY-MM-DD"),
    /** The user who pressed it — set by the server. */
    by: UuidSchema,
    /** In which right — set by the server: the person himself, the rental, or one the rental gave the right (the driver, a crew chief). */
    as: z.enum(["worker", "rental", "granted"]),
    /** The moment it was pressed. */
    at: z.string().datetime({ offset: true }),
};
export const CrewWorkdayFactSchema = z
    .discriminatedUnion("kind", [
    /** «Начать» — the worker's. Its id is the shift's. */
    z.object({ kind: z.literal("start"), ...workdayBase }).strict(),
    /** «Закрыть день» — names the shift it closes. */
    z.object({ kind: z.literal("close"), ...workdayBase, shift: UuidSchema }).strict(),
    /** The rental confirms a close (`of`). No times: the hours are from «Начать» to that close. */
    z.object({ kind: z.literal("confirm"), ...workdayBase, of: UuidSchema }).strict(),
    /** The rental takes back the counted close (`of`): the shift is open again. */
    z.object({ kind: z.literal("reopen"), ...workdayBase, of: UuidSchema }).strict(),
    /** The rental joins two of the person's shifts one after the other — A, then B — into one, the gap between them paid. */
    z.object({ kind: z.literal("merge"), ...workdayBase, shifts: z.tuple([UuidSchema, UuidSchema]) }).strict(),
])
    .superRefine((f, ctx) => {
    const issue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    if (f.kind === "start" && f.as !== "worker")
        issue("as", "«Начать» is the worker's");
    if (f.kind === "confirm" && f.as === "worker")
        issue("as", "a worker does not confirm his own hours");
    if ((f.kind === "reopen" || f.kind === "merge") && f.as === "worker")
        issue("as", "the rental reopens and merges; the worker presses «Начать» again");
    if (f.kind === "merge" && f.shifts[0] === f.shifts[1])
        issue("shifts", "two different shifts");
});
export const validateCrewWorkdayFact = (input) => validateWith(CrewWorkdayFactSchema, input);
/**
 * May `who` write this fact? Checked on the fact as the server stamped it, so `as` must follow from who writes: the worker starts and
 * closes HIS OWN hours, as `worker`, and confirms, reopens or merges nothing; the rental closes, confirms, reopens and merges, as
 * `rental`; the driver (when the show's layer says so) and the links it names (a crew chief) do the same as `granted`, on any hours of
 * the show, their own included.
 */
export function crewMayWorkday(fact, who, grant) {
    if ("rental" in who)
        return fact.as === "rental" && fact.kind !== "start";
    if (fact.as === "granted")
        return fact.kind !== "start" && ((grant.driver && who.role === "driver") || grant.tasks.includes(who.task));
    if (fact.as === "worker")
        return fact.task === who.task && (fact.kind === "start" || fact.kind === "close");
    return false;
}
const factOrder = (a, b) => Date.parse(a.at) - Date.parse(b.at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
const factCanon = (f) => JSON.stringify(Object.keys(f).sort().map((k) => [k, f[k]]));
/**
 * Facts from several sources as one log: the union by id, in time order. An id that came with two different contents is dropped
 * entirely (both versions) and named — the same result whatever the order of the inputs.
 */
export function crewWorkdayFactsMerge(...logs) {
    const byId = new Map();
    const bad = new Set();
    for (const log of logs) {
        for (const f of log) {
            const was = byId.get(f.id);
            if (was && factCanon(was) !== factCanon(f))
                bad.add(f.id);
            else
                byId.set(f.id, f);
        }
    }
    return { facts: [...byId.values()].filter((f) => !bad.has(f.id)).sort(factOrder), conflicts: [...bad].sort() };
}
/**
 * One person's shifts, in the order they began. Pure; the same facts in any order give the same shifts.
 *
 * A shift is a «Начать». Its close is the FIRST close of it pressed (by the moment of pressing), whoever pressed it; a close pressed before
 * its «Начать», or after its first close, counts for nothing. Closed first by the rental (or one with the right) — final at once; closed
 * by the worker — final once a confirmation names that close (the first confirmation counts; one naming another close confirms nothing).
 *
 * A «Начать» pressed while one's own shift is open does not count, nor does a close naming it — by the moment of pressing. Final shifts
 * are taken first: a «Начать» whose shift would overlap a final one does not count, so a final shift never moves or disappears. Shifts
 * therefore never overlap. Nothing is guessed: no start without «Начать», no hours without a close.
 *
 * The rental's `reopen` of the counted close opens the shift again (the first close pressed after it counts; a reopen of a close that
 * does not count does nothing). Its `merge` of two shifts one after the other, both closed, makes one final shift from A's «Начать» to
 * B's close — the gap paid; merges apply in the order the rental pressed them, so [A, B] then [B, C] is one shift.
 */
export function crewShifts(facts, at) {
    const merged = crewWorkdayFactsMerge(facts);
    const conflicts = merged.conflicts.filter((id) => facts.some((f) => f.id === id && f.task === at.task));
    const mine = merged.facts.filter((f) => f.task === at.task); // in the order of pressing
    const ms = (iso) => Date.parse(iso);
    const wordOf = (f) => f.as !== "worker";
    const candidates = mine
        .filter((f) => f.kind === "start")
        .map((st) => {
        // In the order of pressing: the first close counts; the rental's reopen of THAT close opens the shift again, and the first close
        // pressed after it counts.
        let close = null;
        for (const f of mine) {
            if (f.kind === "close" && f.shift === st.id && ms(f.at) >= ms(st.at) && close === null)
                close = f;
            else if (f.kind === "reopen" && wordOf(f) && close !== null && f.of === close.id)
                close = null;
        }
        const confirm = close ? (mine.find((f) => f.kind === "confirm" && f.of === close.id) ?? null) : null;
        const final = close !== null && (close.as !== "worker" || confirm !== null);
        return { start: st, close, confirm, final, from: ms(st.at), to: close ? ms(close.at) : Infinity };
    });
    // Final shifts first, then the rest — each in the order of its «Начать»; one that would overlap a shift already counted does not count.
    const overlaps = (a, b) => a.from < b.to && b.from < a.to;
    const counted = [];
    for (const group of [candidates.filter((c) => c.final), candidates.filter((c) => !c.final)]) {
        for (const c of group)
            if (!counted.some((k) => overlaps(k, c)))
                counted.push(c);
    }
    counted.sort((a, b) => a.from - b.from || (a.start.id < b.start.id ? -1 : 1));
    let joined = counted.map((c) => ({ parts: [c], merge: null }));
    for (const m of mine.filter((f) => f.kind === "merge" && wordOf(f))) {
        const ia = joined.findIndex((j) => j.parts.some((p) => p.start.id === m.shifts[0]));
        const ib = joined.findIndex((j) => j.parts.some((p) => p.start.id === m.shifts[1]));
        if (ia < 0 || ib !== ia + 1)
            continue; // not his, or not one after the other
        if (joined[ia].parts.at(-1).close === null || joined[ib].parts.at(-1).close === null)
            continue; // a shift still open
        joined = [...joined.slice(0, ia), { parts: [...joined[ia].parts, ...joined[ib].parts], merge: m }, ...joined.slice(ib + 1)];
    }
    const hoursOf = (from, to) => Math.round(((ms(to) - ms(from)) / 3_600_000) * 100) / 100;
    const historyOf = (c) => mine.filter((f) => f.id === c.start.id ||
        (f.kind === "close" && f.shift === c.start.id) ||
        ((f.kind === "confirm" || f.kind === "reopen") && mine.some((k) => k.kind === "close" && k.shift === c.start.id && k.id === f.of)) ||
        (f.kind === "merge" && f.shifts.includes(c.start.id)));
    const shifts = joined.map(({ parts, merge }) => {
        const first = parts[0];
        const last = parts.at(-1);
        const { close } = last;
        const st = first.start;
        const by = merge ?? (parts.length === 1 ? (first.confirm ?? (first.final ? close : null)) : null);
        const final = merge !== null || first.final;
        const ids = new Set(parts.flatMap((p) => historyOf(p).map((f) => f.id)));
        return {
            id: st.id,
            task: at.task,
            day: st.day,
            state: close ? (final ? "confirmed" : "closed") : "open",
            start: { time: st.at, by: st.by },
            close: close ? { time: close.at, source: close.as, by: close.by } : null,
            confirmed: by ? { by: by.by, at: by.at, source: by.as } : null,
            hours: close ? hoursOf(st.at, close.at) : null,
            history: mine.filter((f) => ids.has(f.id)),
        };
    });
    return { shifts, conflicts };
}
/** The sum of hours — shifts never overlap, so it is a plain sum. */
const sumHours = (xs) => {
    const closed = xs.filter((x) => x.hours !== null);
    return closed.length === 0 ? null : Math.round(closed.reduce((total, x) => total + x.hours, 0) * 100) / 100;
};
/** A person's shifts begun on one date, all their hours and the confirmed ones. */
export function crewWorkday(facts, at) {
    const all = crewShifts(facts, at);
    const shifts = all.shifts.filter((x) => x.day === at.day);
    return {
        task: at.task,
        day: at.day,
        shifts,
        hours: sumHours(shifts),
        confirmedHours: sumHours(shifts.filter((x) => x.state === "confirmed")),
        conflicts: all.conflicts.filter((id) => facts.some((f) => f.id === id && f.day === at.day)),
    };
}
export const validateCrewTickInput = (input) => validateWith(CrewTickInputSchema, input);
//# sourceMappingURL=crew-pipeline.js.map